import {
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Alert,
  Box,
  Dialog,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  capitalize,
  Collapse,
} from "@mui/material";
import moment from "moment";
import React from "react";
import {
  PersonCircle,
  PlusSquareFill,
  Hospital,
  Save,
  XCircle,
  Calendar2Week,
  CalendarPlusFill,
} from "react-bootstrap-icons";
import { UserContext } from "../../components/layout/Layout";
import Utils from "../../utils/utils";
import {
  DesktopDatePicker,
  LocalizationProvider,
  TimePicker,
} from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { v2 } from "../../api/v2";
import Spinner from "../../components/spinner/spinner";

// Patient-facing reschedule. v2 owns slot validation (the edge reschedule
// re-checks the slot is free, excluding this appointment), so the client only
// has to collect a future date/time and submit — no legacy doctor-timing /
// holiday math (the v2 appointment row doesn't carry that shape anyway).
export default class RescheduleAnAppointment extends React.Component<any, any> {
  static contextType = UserContext;

  constructor(props: any) {
    super(props);
    const startDateTime = moment(this.props.apptDetails.start);
    this.state = {
      origStartDateTime: startDateTime,
      startDateTime: startDateTime,
      isLoading: false,
      saveError: false,
      isExpiredDate: false,
      slotTaken: false,
    };
  }

  onRescheduleAppointment() {
    const selected = moment(this.state.startDateTime);
    if (!selected.isValid() || selected.isSameOrBefore(moment())) {
      this.setState({ isExpiredDate: true, isLoading: false });
      setTimeout(() => this.setState({ isExpiredDate: false }), 2500);
      return;
    }
    const appt = this.props.apptDetails;
    const scheduledAt = selected.format("YYYY-MM-DDTHH:mm:00");
    const tenant =
      appt.heId || this.props.referenceCode || this.context?.["heid"];
    // Patients move the time only; keep the existing clinic (null = unchanged).
    v2.rescheduleAppointment(appt.apptId, scheduledAt, null, tenant)
      .then(() => {
        this.setState({ isLoading: false });
        this.props.onRescheduleSuccess();
      })
      .catch((error) => {
        const detail = error?.response?.data?.detail;
        const slotTaken = detail === "slot_no_longer_available";
        this.setState({ isLoading: false, saveError: !slotTaken, slotTaken });
        setTimeout(
          () => this.setState({ saveError: false, slotTaken: false }),
          3000
        );
      });
  }

  render() {
    const appt = this.props.apptDetails;
    const patientName =
      (this.context?.["first_name"] || "") +
      " " +
      (this.context?.["last_name"] || "");
    const appointmentType =
      appt.modeOfAdmission === "Online" ? "Online" : "Personal visit";
    let doctorName = capitalize(appt.doctor?.firstName || "");
    if (appt.doctor?.lastName) {
      doctorName += " " + appt.doctor.lastName;
    }
    return (
      <Dialog
        fullWidth
        maxWidth="md"
        open={true}
        aria-labelledby="responsive-dialog-title"
      >
        <DialogTitle
          sx={{ backgroundColor: "#1976d2", color: "white" }}
          id="responsive-dialog-title"
        >
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Box sx={{ display: "flex", mr: 2 }}>
              <Calendar2Week size={25} />
            </Box>
            Reschedule appointment
          </Box>
        </DialogTitle>
        <DialogContent>
          {this.state.isLoading && <Spinner />}
          <Grid container sx={{ mt: 2 }}>
            <Grid item lg={3}>
              <ListItem>
                <ListItemAvatar sx={{ minWidth: "48px" }}>
                  <PersonCircle color="#1976d2" size={30} />
                </ListItemAvatar>
                <ListItemText primary={doctorName} />
              </ListItem>
            </Grid>
            <Grid item lg={3}>
              <ListItem sx={{ mt: 1 }}>
                <ListItemAvatar sx={{ minWidth: "48px" }}>
                  <PlusSquareFill color="#1976d2" size={30} />
                </ListItemAvatar>
                <ListItemText primary={appointmentType} />
              </ListItem>
            </Grid>
            {appt?.locName && (
              <Grid item lg={3}>
                <ListItem sx={{ mt: 1 }}>
                  <ListItemAvatar sx={{ minWidth: "48px" }}>
                    <Hospital color="#1976d2" size={30} />
                  </ListItemAvatar>
                  <ListItemText primary={appt.locName} />
                </ListItem>
              </Grid>
            )}
            <Grid item lg={3}>
              <ListItem>
                <ListItemAvatar>
                  <Avatar {...Utils.stringAvatar(patientName)} />
                </ListItemAvatar>
                <ListItemText
                  primary={patientName}
                  secondary={this.context?.["patient_no"]}
                />
              </ListItem>
            </Grid>
            {!appt?.locName && (
              <Grid item lg={3}>
                <span></span>
              </Grid>
            )}
            <Grid item lg={3}>
              <ListItem>
                <ListItemAvatar>
                  <CalendarPlusFill color="#1976d2" size={30} />
                </ListItemAvatar>
                <ListItemText
                  primary={moment(appt.start).format("DD/MM/YYYY")}
                  secondary={moment(appt.start).format("hh:mm A")}
                />
              </ListItem>
            </Grid>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <Grid item lg={3} sx={{ pt: 2, pl: 2 }}>
                <DesktopDatePicker
                  label="New appointment date"
                  inputFormat="dd/MM/yyyy"
                  value={this.state.startDateTime}
                  onChange={(startDateTime) => {
                    this.setState({ startDateTime });
                  }}
                  renderInput={(params) => <TextField {...params} />}
                />
              </Grid>
              <Grid item lg={3} sx={{ pt: 2, pl: 2 }}>
                <TimePicker
                  label="New appointment time"
                  value={this.state.startDateTime}
                  minutesStep={5}
                  onChange={(startDateTime) => {
                    this.setState({ startDateTime });
                  }}
                  renderInput={(params) => <TextField {...params} />}
                />
              </Grid>
              <Collapse in={this.state.isExpiredDate}>
                <Alert sx={{ ml: 1, mt: 2 }} severity="error">
                  Please pick a future date and time.
                </Alert>
              </Collapse>
              <Collapse in={this.state.slotTaken}>
                <Alert sx={{ ml: 1, mt: 2 }} severity="error">
                  That slot is no longer available. Please pick another time.
                </Alert>
              </Collapse>
              <Collapse in={this.state.saveError}>
                <Alert sx={{ ml: 1, mt: 2 }} severity="error">
                  Couldn't reschedule the appointment. Please try again.
                </Alert>
              </Collapse>
            </LocalizationProvider>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            autoFocus
            variant="contained"
            color="error"
            endIcon={<XCircle />}
            onClick={() => {
              this.props.onDiscardReschedule();
            }}
          >
            Discard
          </Button>
          <Button
            variant="contained"
            autoFocus
            disabled={
              this.state.isLoading ||
              moment(this.state.origStartDateTime).isSame(
                moment(this.state.startDateTime)
              )
            }
            endIcon={<Save />}
            onClick={() => {
              this.setState({ isLoading: true });
              this.onRescheduleAppointment();
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    );
  }
}
